/**
 * Dinero en centavos enteros.
 *
 * Todo monto de PayBae —base de datos, API y cliente— es un entero de centavos.
 * Nunca un flotante: `0.1 + 0.2 !== 0.3` en coma flotante binaria, y en una app
 * de finanzas ese error se acumula hasta descuadrar un saldo.
 *
 * La conversión a pesos ocurre solo al formatear para la pantalla.
 */

/** Monto en centavos. Entero, puede ser negativo. */
export type Cents = number

export const CENTS_PER_UNIT = 100

/** Máximo representable con exactitud: ~90 mil millones de pesos. */
const MAX_SAFE_CENTS = Number.MAX_SAFE_INTEGER

export function assertCents(value: number, label = 'monto'): Cents {
  if (!Number.isInteger(value)) {
    throw new RangeError(`El ${label} debe ser un entero de centavos, se recibió ${value}`)
  }
  if (Math.abs(value) > MAX_SAFE_CENTS) {
    throw new RangeError(`El ${label} excede el rango exacto de JavaScript`)
  }
  return value
}

/** Pesos a centavos. `pesos(1234.56) === 123456`. */
export function pesos(amount: number): Cents {
  return Math.round(amount * CENTS_PER_UNIT)
}

/** Centavos a pesos, solo para gráficas y exportaciones. */
export function toPesos(cents: Cents): number {
  return cents / CENTS_PER_UNIT
}

export function sumCents(...amounts: Cents[]): Cents {
  let total = 0
  for (const a of amounts) total += a
  return total
}

export function sumBy<T>(items: readonly T[], pick: (item: T) => Cents): Cents {
  let total = 0
  for (const item of items) total += pick(item)
  return total
}

export function clampNonNegative(cents: Cents): Cents {
  return cents < 0 ? 0 : cents
}

/**
 * Porcentaje de un monto, redondeado al centavo.
 * `applyPercent(pesos(10000), 20) === pesos(2000)`
 */
export function applyPercent(cents: Cents, percent: number): Cents {
  return Math.round((cents * percent) / 100)
}

/**
 * Reparte un monto entre N partes sin perder ni inventar centavos.
 * El residuo se distribuye entre las primeras partes, así que la suma del
 * resultado siempre es exactamente el total: repartir $10.00 entre tres da
 * [334, 333, 333], no tres veces 333 con un centavo perdido.
 */
export function splitEvenly(total: Cents, parts: number): Cents[] {
  if (!Number.isInteger(parts) || parts <= 0) {
    throw new RangeError(`El número de partes debe ser un entero positivo: ${parts}`)
  }
  const base = Math.floor(total / parts)
  const remainder = total - base * parts
  return Array.from({ length: parts }, (_, i) => base + (i < remainder ? 1 : 0))
}

/**
 * Reparte proporcionalmente a unos pesos dados, ajustando el residuo en la
 * parte de mayor peso para que la suma cuadre al centavo.
 */
export function splitProportionally(total: Cents, weights: readonly number[]): Cents[] {
  if (weights.length === 0) throw new RangeError('Se necesita al menos un peso')
  const totalWeight = weights.reduce((a, b) => a + b, 0)
  if (totalWeight <= 0) return splitEvenly(total, weights.length)

  const shares = weights.map((w) => Math.floor((total * w) / totalWeight))
  let remainder = total - shares.reduce((a, b) => a + b, 0)

  // El residuo va a las partes de mayor peso, en orden.
  const order = weights
    .map((w, i) => ({ w, i }))
    .sort((a, b) => b.w - a.w)
    .map((x) => x.i)

  let cursor = 0
  const step = remainder >= 0 ? 1 : -1
  while (remainder !== 0) {
    const idx = order[cursor % order.length]!
    shares[idx] = shares[idx]! + step
    remainder -= step
    cursor++
  }
  return shares
}

/**
 * Lee un monto escrito por una persona: «1,234.56», «$1234.56», «1 234,56».
 * Devuelve `null` si no hay un número reconocible, para que el formulario
 * muestre el error en vez de guardar un cero silencioso.
 */
export function parseAmount(input: string): Cents | null {
  const trimmed = input.trim()
  if (trimmed === '') return null

  let cleaned = trimmed.replace(/[^\d.,-]/g, '')
  if (cleaned === '' || cleaned === '-') return null

  const lastComma = cleaned.lastIndexOf(',')
  const lastDot = cleaned.lastIndexOf('.')

  if (lastComma > lastDot) {
    // Formato europeo: la coma es el decimal.
    cleaned = cleaned.replace(/\./g, '').replace(',', '.')
  } else {
    // Formato mexicano: la coma agrupa millares.
    cleaned = cleaned.replace(/,/g, '')
  }

  const value = Number(cleaned)
  if (!Number.isFinite(value)) return null
  return pesos(value)
}

export interface FormatMoneyOptions {
  /** Oculta los centavos cuando son cero. Útil en gráficas y listas densas. */
  hideZeroCents?: boolean
  /** Antepone `+` a los montos positivos. */
  showSign?: boolean
  /** Omite el símbolo de moneda. */
  omitSymbol?: boolean
  locale?: string
  currency?: string
}

export function formatMoney(cents: Cents, options: FormatMoneyOptions = {}): string {
  const {
    hideZeroCents = false,
    showSign = false,
    omitSymbol = false,
    locale = 'es-MX',
    currency = 'MXN',
  } = options

  const fractionDigits = hideZeroCents && cents % CENTS_PER_UNIT === 0 ? 0 : 2

  const formatted = new Intl.NumberFormat(locale, {
    style: omitSymbol ? 'decimal' : 'currency',
    currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(toPesos(cents))

  if (showSign && cents > 0) return `+${formatted}`
  return formatted
}

/** Forma compacta para ejes de gráficas: `$12.3k`, `$1.2M`. */
export function formatMoneyCompact(cents: Cents, locale = 'es-MX', currency = 'MXN'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(toPesos(cents))
}

/**
 * Porcentaje de avance, acotado a 0–100 y tolerante a metas en cero.
 * Alimenta las barras de progreso de metas de ahorro y de uso de tarjeta.
 */
export function percentOf(part: Cents, whole: Cents): number {
  if (whole <= 0) return 0
  return Math.min(100, Math.max(0, (part / whole) * 100))
}
